import ActionConstants from '../constants/';
import {
  sendChatMessageApi,
  streamChatMessageApi,
  clearChatHistoryApi,
  getSuggestionsApi,
  createTicketApi,
  initChatbotApi,
  fetchChatTicketsApi,
  fetchChatHistoryApi,
} from '../../api/chatbot';

export const initChatbotAction = () => {
  const payload = {
    serviceMethod: initChatbotApi.bind(null),
    actionTypeSuccess: 'INIT_CHATBOT_SUCCESS',
    actionTypeFailure: 'INIT_CHATBOT_FAILURE',
    actionTypeRequest: 'INIT_CHATBOT_REQUEST',
    hideLoader: true,
  };
  return { type: ActionConstants.INIT_CHATBOT, payload };
};

/**
 * Loads an older page of chat history (scroll-back / "load more").
 * `before` is the cursor returned by the previous page (init or a prior load-more call).
 */
export const loadMoreChatHistoryAction = (before: string) => {
  const payload = {
    serviceMethod: fetchChatHistoryApi.bind(null, before),
    actionTypeSuccess: ActionConstants.LOAD_MORE_CHAT_HISTORY_SUCCESS,
    actionTypeFailure: ActionConstants.LOAD_MORE_CHAT_HISTORY_FAILURE,
    actionTypeRequest: ActionConstants.LOAD_MORE_CHAT_HISTORY_REQUEST,
    hideLoader: true,
  };
  return { type: ActionConstants.LOAD_MORE_CHAT_HISTORY, payload };
};

export const sendChatMessageAction = (
  message: string,
  currentPage?: string,
) => {
  const payload = {
    serviceMethod: sendChatMessageApi.bind(null, message, currentPage),
    actionTypeSuccess: ActionConstants.SEND_CHAT_MESSAGE_SUCCESS,
    actionTypeFailure: ActionConstants.SEND_CHAT_MESSAGE_FAILURE,
    actionTypeRequest: ActionConstants.SEND_CHAT_MESSAGE_REQUEST,
    extra: { message },
    hideLoader: true,
  };
  return { type: ActionConstants.SEND_CHAT_MESSAGE, payload };
};

/**
 * Streaming variant of sendChatMessageAction.
 *
 * Opens an SSE stream to the BFF `/chatbot/chat/stream` endpoint and dispatches
 * lifecycle actions as events arrive:
 *   - CHAT_STREAM_START: marks loading, creates an empty assistant message
 *   - CHAT_STREAM_CHUNK: appends content to the last assistant message
 *   - CHAT_STREAM_END:   clears loading
 *   - CHAT_STREAM_ERROR: sets an error message + clears loading
 *
 * NOTE: This project's store is wired with redux-saga only (no redux-thunk),
 * so this is a plain function the *component* calls directly (not a dispatched
 * action object). The component is responsible for calling it and managing
 * the returned AbortController (e.g. cancelling on unmount / new send).
 *
 * @param onOpenTicketForm Invoked when the bot emits a `ticketCreate` event
 *   (explicit merchant ticket request — no confirmation banner). The component
 *   passes its `handleOpenTicketForm` so the prefilled ticket form opens directly.
 */
export const startChatStream = (
  dispatch: (a: any) => void,
  message: string,
  currentPage?: string,
  aiHelperFieldLabel?: string | null,
  verificationErrorContext?: { errors: string[]; route: string } | null,
  onOpenTicketForm?: () => void,
): AbortController => {
  dispatch({ type: ActionConstants.CHAT_STREAM_START, payload: { current_step: currentPage } });

  return streamChatMessageApi(message, currentPage, (event) => {
    switch (event.type) {
      case 'chunk':
        dispatch({
          type: ActionConstants.CHAT_STREAM_CHUNK,
          payload: { content: event.content },
        });
        break;
      case 'end':
        dispatch({ type: ActionConstants.CHAT_STREAM_END });
        break;
      case 'error':
        dispatch({
          type: ActionConstants.CHAT_STREAM_ERROR,
          payload: { content: event.content, current_step: currentPage },
        });
        break;
      case 'ticketConfirm':
        // Bot suggests ticket creation — show inline form with prefilled fields
        dispatch({
          type: ActionConstants.CHAT_TICKET_CONFIRM,
          payload: {
            summary: event.summary || '',
            merchantId: event.merchantId || '',
            title: event.title || event.summary || '',
            description: event.description || event.summary || '',
            category: event.category || 'other',
            issueType: event.issueType || 'OTHER',
            processStep: event.processStep || currentPage || '',
            priority: event.priority || 'medium',
          },
        });
        break;
      case 'ticketCreate':
        dispatch({
          type: ActionConstants.CHAT_TICKET_CONFIRM,
          payload: {
            summary: event.summary || '',
            merchantId: event.merchantId || '',
            title: event.title || event.summary || '',
            description: event.description || event.summary || '',
            category: event.category || 'other',
            issueType: event.issueType || 'OTHER',
            processStep: event.processStep || currentPage || '',
            priority: event.priority || 'medium',
          },
        });
        onOpenTicketForm?.();
        break;
      default:
        break;
    }
  }, aiHelperFieldLabel, verificationErrorContext);
};

export const clearChatHistoryAction = () => {
  const payload = {
    serviceMethod: clearChatHistoryApi.bind(null),
    actionTypeSuccess: ActionConstants.CLEAR_CHAT_HISTORY_SUCCESS,
    actionTypeFailure: ActionConstants.CLEAR_CHAT_HISTORY_FAILURE,
    actionTypeRequest: ActionConstants.CLEAR_CHAT_HISTORY_REQUEST,
    hideLoader: true,
  };
  return { type: ActionConstants.CLEAR_CHAT_HISTORY, payload };
};

export const getSuggestionsAction = (current_step) => {
  const payload = {
    serviceMethod: getSuggestionsApi.bind(null, current_step),
    actionTypeSuccess: ActionConstants.GET_CHAT_SUGGESTIONS_SUCCESS,
    actionTypeFailure: ActionConstants.GET_CHAT_SUGGESTIONS_FAILURE,
    actionTypeRequest: ActionConstants.GET_CHAT_SUGGESTIONS_REQUEST,
    hideLoader: true,
  };
  return { type: ActionConstants.GET_CHAT_SUGGESTIONS, payload };
};

export const toggleChatWidget = () => ({
  type: ActionConstants.TOGGLE_CHAT_WIDGET,
  payload: {},
});

export const addLocalChatMessage = (role: 'user' | 'assistant', content: string, currentStep?: string) => ({
  type: ActionConstants.ADD_LOCAL_CHAT_MESSAGE,
  payload: { role, content, current_step: currentStep },
});

/**
 * Sets the AI helper field label context.
 * This label will be sent with the next user message to the server
 * so the bot knows which field the user needs help with.
 */
export const setAiHelperContext = (fieldLabel: string) => ({
  type: ActionConstants.SET_AI_HELPER_CONTEXT,
  payload: { fieldLabel },
});

/**
 * Clears the AI helper field label context (typically after the first message is sent).
 */
export const clearAiHelperContext = () => ({
  type: ActionConstants.CLEAR_AI_HELPER_CONTEXT,
  payload: {},
});

/**
 * Sets error context gathered from the verification flow (DOM form errors + API errors).
 * This is sent invisibly to the bot when the user opens chat from an error-state tooltip.
 * The error message itself is NOT shown in the chat UI or saved in history.
 */
export const setVerificationErrorContext = (context: { errors: string[]; route: string }, isAlreadyOpened: boolean) => ({
  type: ActionConstants.SET_VERIFICATION_ERROR_CONTEXT,
  payload: { ...context, isAlreadyOpened },
});

export const clearVerificationErrorContext = () => ({
  type: ActionConstants.CLEAR_VERIFICATION_ERROR_CONTEXT,
  payload: {},
});

export const resetChatState = () => ({
  type: ActionConstants.RESET_CHAT_STATE,
  payload: {},
});

// ---- Ticket actions ----
export const dismissTicketConfirm = () => ({
  type: ActionConstants.CHAT_TICKET_CONFIRM_DISMISS,
  payload: {},
});

export const cancelTicketConfirm = () => ({
  type: ActionConstants.CHAT_TICKET_CONFIRM_CANCEL,
  payload: {},
});

export const toggleTicketList = () => ({
  type: ActionConstants.CHAT_TOGGLE_TICKET_LIST,
  payload: {},
});

export const fetchTicketList = () => {
  const payload = {
    serviceMethod: fetchChatTicketsApi.bind(null),
    actionTypeSuccess: ActionConstants.CHAT_TICKET_LIST_SUCCESS,
    actionTypeFailure: ActionConstants.CHAT_TICKET_LIST_FAILURE,
    actionTypeRequest: ActionConstants.CHAT_TICKET_LIST_FETCH,
    hideLoader: true,
  };
  return { type: ActionConstants.CHAT_TICKET_LIST_FETCH, payload };
};

export interface TicketPayload {
  title: string;
  description: string;
  comment?: string;
  issueType?: string;
  processStep?: string;
  journeyType?: string;
  leadId?: string;
  opportunityId?: string;
  applicationId?: string;
  systemErrorCode?: string;
  affectedService?: string;
  category?: string;
  timestamp?: Date;
}

export const confirmCreateTicket = (ticketPayload: TicketPayload) => {
  const payload = {
    serviceMethod: createTicketApi.bind(null, ticketPayload),
    actionTypeSuccess: ActionConstants.CHAT_TICKET_CREATED,
    actionTypeFailure: ActionConstants.CHAT_TICKET_CREATE_FAILED,
    actionTypeRequest: ActionConstants.CHAT_TICKET_CREATING,
    extra: ticketPayload,
    hideLoader: true,
  };
  return { type: ActionConstants.CHAT_TICKET_CONFIRM_SUBMIT, payload };
};

/**
 * Direct ticket creation (no confirmation form shown).
 * Not wired to the SSE stream anymore — a `ticketCreate` event now opens the
 * prefilled ticket form via `onOpenTicketForm`. Kept for manual/programmatic use.
 */
export const createTicketDirect = (ticketPayload: TicketPayload) => {
  const payload = {
    serviceMethod: createTicketApi.bind(null, ticketPayload),
    actionTypeSuccess: ActionConstants.CHAT_TICKET_CREATED,
    actionTypeFailure: ActionConstants.CHAT_TICKET_CREATE_FAILED,
    actionTypeRequest: ActionConstants.CHAT_TICKET_CREATING,
    extra: ticketPayload,
    hideLoader: true,
  };
  return { type: ActionConstants.CHAT_TICKET_CREATE_DIRECT, payload };
};
